import { fuelPerKmGrosze } from '@app/shared/price-calc';
import { parsePln, plnToInput } from '../catalog/money';
import { parseScaled } from '../ui/numbers';
import type { FuelKind } from './types';

/** Owner 2026-10-24: own transport's cost per km from the fuel prices, counted live in the settings form as the server does. */
export const FUEL_LABELS: Record<FuelKind, string> = { pb95: 'Pb95', on: 'ON', lpg: 'LPG' };
export const FUEL_KINDS: readonly FuelKind[] = ['pb95', 'on', 'lpg'];

/** 75 ml/km → "7,5" (l/100 km). */
export const litresText = (mlPerKm: number) => (mlPerKm % 10 === 0 ? String(mlPerKm / 10) : `${Math.floor(mlPerKm / 10)},${mlPerKm % 10}`);
/** "2026-10-19" → "19.10.2026". */
export const dayText = (day: string) => day.split('-').reverse().join('.');

/** "7,5" (l/100 km) → 75 ml/km; empty → null (not entered). Throws a Polish message otherwise. */
export function parseConsumption(text: string): number | null {
  if (text.trim() === '') return null;
  const v = parseScaled(text, 1, { maxIntegerDigits: 3 });
  if (v === null || v > 1000) throw new Error('Spalanie: od 0 do 100 l/100 km, najwyżej jedno miejsce po przecinku');
  return v;
}

export type LiveRate = { perKmGrosze: number; basis: string; mode: 'paliwo' | 'stawka' | 'brak-ceny' };

export function vehicleRate(input: { fuel: FuelKind; mlPerKm: number | null; extraPerKmGrosze: number; legacyPerKmGrosze: number; price: { effectiveGrosze: number | null; manual: boolean } }): LiveRate {
  const legacy = `${plnToInput(input.legacyPerKmGrosze)} zł/km`;
  if (input.mlPerKm === null) return { perKmGrosze: input.legacyPerKmGrosze, basis: `dotychczasowa stawka ${legacy} — wpisz spalanie, by liczyć z cen paliwa`, mode: 'stawka' };
  const price = input.price.effectiveGrosze;
  if (price === null && input.mlPerKm > 0) return { perKmGrosze: input.legacyPerKmGrosze, basis: `brak ceny ${FUEL_LABELS[input.fuel]} — dotychczasowa stawka ${legacy}`, mode: 'brak-ceny' };
  const perKmGrosze = fuelPerKmGrosze({ mlPerKm: input.mlPerKm, priceGroszePerLitre: price ?? 0, extraPerKmGrosze: input.extraPerKmGrosze });
  const fuel = price === null ? '' : `${FUEL_LABELS[input.fuel]} ${plnToInput(price)} zł/l × ${litresText(input.mlPerKm)} l/100 km${input.price.manual ? ' (cena wpisana ręcznie)' : ''}`;
  // owner 2026-10-24 10:22: the extra per km is the vehicle's wear („zużycie auta”)
  const extra = input.extraPerKmGrosze > 0 ? `${plnToInput(input.extraPerKmGrosze)} zł/km zużycie` : '';
  return { perKmGrosze, basis: [fuel, extra].filter(Boolean).join(' + ') || '0 l/100 km', mode: 'paliwo' };
}

/** Owner 2026-10-24 10:22 / 16:15: „Stała kwota za wydarzenie” as the form shows it ('' while hidden by the field rules). */
export const perDayText = (grosze: number | undefined) => (grosze === undefined ? '' : plnToInput(grosze));
/** The typed fixed amount per event: empty is 0; at most 10 000 zł (as the server). Throws a Polish message otherwise. */
export function parsePerDay(text: string): number {
  if (text.trim() === '') return 0;
  const grosze = parsePln(text);
  if (grosze > 1_000_000) throw new Error('Stała kwota za wydarzenie: od 0 do 10 000 zł');
  return grosze;
}

/**
 * Review I2: what a save sends — only fields the settings answer has (a field hidden by the field rules is missing
 * there, and writing it would be refused) and whose value changed.
 */
export function settingsChanges(seen: Record<string, unknown>, form: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(form).filter(([k, v]) => k in seen && seen[k] !== v));
}
